import { registerComponent, debounce } from "helpers";
import { WYATTElement } from "wyatt";

registerComponent(
  "wyatt-slider",
  class WyattSlider extends WYATTElement {
    constructor() {
      super();

      this.currentIndex = 0;
      this.slider = this.querySelector("[data-slider]");
      this.slides = Array.from(this.slider.children);
      this.slideCount = this.slides.length;
      this.showControls = this.config.hideControls
        ? false
        : this.slideCount > 1 && this.slideCount > this.config.slidesToShow;
    }

    connectedCallback() {
      this.setupData();
      this.addControls();
      this.addNavigation();
      this.bindEvents();
      this.connectSliders();

      this.goTo(this.config.startAt, "instant");
      this.setAttribute("ready", true);
    }

    // SETUP

    setupData() {
      this.slides.forEach((slide, index) => {
        slide.dataset.slide = index;
        slide.classList.add("slider__slide");
      });

      // Slides are sized at 100% / slidesToShow, so overflow exists exactly
      // when there are more slides than shown. Only then does the scroll
      // region take keyboard focus (arrow keys scroll it natively); a
      // non-scrollable slider would just add a pointless tab stop.
      if (this.slideCount > this.config.slidesToShow) {
        this.slider.tabIndex = 0;
      }
    }

    addControls() {
      if (!this.showControls) {
        return;
      }

      const template = this.querySelector("[data-controls]").content;
      const controls = document.importNode(template, true);
      this.appendChild(controls);

      this.setAttribute("has-controls", true);
      this.controlButtons = this.querySelectorAll(
        "[data-slider-control-button]",
      );
    }

    addNavigation() {
      if (!this.config.showNavigation) {
        return;
      }

      const navigation = document.createElement("div");
      const navigationButton = this.querySelector(
        "[data-navigation-button]",
      ).content;

      this.slides.forEach((slide, index) => {
        const button = navigationButton.cloneNode(true);

        button.querySelector(".slider__dot").textContent = `Slide ${index + 1}`;
        button.querySelector(
          "[data-slider-navigation-button]",
        ).dataset.sliderNavigationButton = index;

        navigation.appendChild(button);
      });

      navigation.classList.add("slider__navigation");
      this.appendChild(navigation);

      this.navigationButtons = navigation.querySelectorAll(
        "[data-slider-navigation-button]",
      );
    }

    bindEvents() {
      this.slider.addEventListener(
        "scroll",
        debounce((event) => {
          this.handleScroll(event);
        }, 300).bind(this),
      );

      if (this.showControls) {
        this.controlButtons.forEach((button) => {
          button.addEventListener("click", this.handleControlClick.bind(this));
        });
      }

      if (this.config.showNavigation) {
        this.navigationButtons.forEach((button) => {
          button.addEventListener(
            "click",
            this.handleNavigationClick.bind(this),
          );
        });
      }
    }

    connectSliders() {
      const sliderToNavigate = document.querySelector(
        this.config.navigationFor,
      );

      if (sliderToNavigate) {
        this.sliderToNavigate = sliderToNavigate;
        sliderToNavigate.navigatedBy = this;

        this.slides.forEach((slide) => {
          this.updateSlideAsNavigation(slide);

          slide
            .querySelector("button")
            .addEventListener(
              "click",
              this.handleSlideAsNavigationClick.bind(this),
            );
        });
      }
    }

    updateSlideAsNavigation(slide) {
      const button = document.createElement("button");
      const html = slide.innerHTML;

      button.type = "button";
      button.classList.add("button-reset", "slider__slide-button");
      button.innerHTML = html;

      slide.innerHTML = "";
      slide.appendChild(button);
    }

    // HANDLERS

    handleControlClick(event) {
      const button = event.currentTarget;
      const direction = button.dataset.sliderControlButton;
      const newIndex =
        direction === "next" ? this.currentIndex + 1 : this.currentIndex - 1;

      this.goTo(newIndex);

      if (this.navigatedBy) {
        this.navigatedBy.goTo(newIndex);
      }

      if (this.sliderToNavigate) {
        this.sliderToNavigate.goTo(newIndex);
      }
    }

    handleNavigationClick(event) {
      const button = event.currentTarget;
      const newIndex = parseInt(button.dataset.sliderNavigationButton, 10);

      this.goTo(newIndex);
    }

    handleScroll(event) {
      if (this === this.sliderToNavigate?.navigatedBy) {
        return;
      }

      /*
        scrollLeft and offsetWidth round the actual values, therefor a buffer is needed
        to ensure the position will be slightly larger than the multiples of slideWidth
      */
      const buffer = this.slideCount;
      const position = this.slider.scrollLeft + buffer;
      const slideWidth = this.slider.querySelector(
        '[data-slide]:not([class*="hidden"])',
      ).offsetWidth;
      const newIndex = Math.floor(position / slideWidth);

      if (newIndex === this.currentIndex) {
        return;
      }

      event.preventDefault();

      this.updateState(newIndex);

      if (this.navigatedBy) {
        this.navigatedBy.goTo(newIndex);
      }
    }

    handleSlideAsNavigationClick(event) {
      const slide = event.currentTarget.closest("[data-slide]");
      const index = parseInt(slide.dataset.slide, 10);

      event.preventDefault();

      this.sliderToNavigate.goTo(index);
      this.updateState(index);
    }

    // UPDATES

    updateState(newIndex) {
      // Clamp: handleScroll can compute a newIndex outside [0, slideCount-1]
      // during iOS overscroll bounce, propagating to DOM lookups that throw.
      const clampedIndex = Math.max(0, Math.min(newIndex, this.slideCount - 1));

      this.updateSliderClass(clampedIndex);
      this.updateNavigationButtons(clampedIndex);
      this.updateCurrentSlide(clampedIndex);
      this.updateControlButtons();
    }

    updateSliderClass(newIndex) {
      this.classList.remove(`slider--at-slide-${this.currentIndex}`);
      this.classList.add(`slider--at-slide-${newIndex}`);
    }

    updateNavigationButtons(newIndex) {
      if (!this.config.showNavigation) {
        return;
      }

      const currentButton = this.querySelector(
        `[data-slider-navigation-button="${this.currentIndex}"]`,
      );
      const newButton = this.querySelector(
        `[data-slider-navigation-button="${newIndex}"]`,
      );

      // Defensive: callers clamp newIndex, but guard against the buttons
      // having been removed externally before updating attributes.
      if (currentButton) {
        currentButton.setAttribute("active", false);
      }
      if (newButton) {
        newButton.setAttribute("active", true);
      }
    }

    updateCurrentSlide(newIndex) {
      this.slides[this.currentIndex].setAttribute("active", false);
      this.currentIndex = newIndex;
      this.slides[this.currentIndex].setAttribute("active", true);
    }

    updateControlButtons() {
      if (!this.showControls) {
        return;
      }

      const nextSlideCount = () => {
        const lastShownSlide = this.currentIndex + this.config.slidesToShow;

        return this.slideCount - lastShownSlide < 0
          ? 0
          : this.slideCount - lastShownSlide;
      };

      this.controlButtons.forEach((button) => {
        const direction = button.dataset.sliderControlButton;
        const onFirstSlide = this.currentIndex === 0;
        const onLastSlide = this.currentIndex + 1 === this.slideCount;

        if (direction === "previous" && onFirstSlide) {
          button.disabled = true;
        } else if (
          (direction === "next" && onLastSlide) ||
          (direction === "next" &&
            nextSlideCount() === 0 &&
            !this.sliderToNavigate)
        ) {
          button.disabled = true;
        } else {
          button.disabled = false;
        }
      });
    }

    goTo(newIndex, behavior = "smooth") {
      // Clamp: handleControlClick computes currentIndex +/- 1 without bounds,
      // and external callers (e.g. sliderToNavigate.goTo) may pass any value.
      const clampedIndex = Math.max(0, Math.min(newIndex, this.slideCount - 1));

      const newSlide = this.slider.querySelector(
        `[data-slide="${clampedIndex}"]`,
      );

      if (!newSlide) {
        return;
      }

      const width = newSlide.offsetWidth;
      const slideDifference = clampedIndex - this.currentIndex;

      this.slider.scrollBy({
        top: 0,
        left: width * slideDifference,
        behavior,
      });

      this.updateState(clampedIndex);
    }
  },
);
